import { Fragment } from 'react'

import { FormattedMessage, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router'
import { Link } from 'react-router-dom'

import { CampaignMenuActionsProvider } from '@components/Campaign/Menu'
import HeadTag from '@components/UI/HeadTag'
import TitleBar, { HeaderBackButton, TitleBarMobile } from '@components/UI/TitleBar'
import { HeaderTitle } from '@components/UI/TitleBar/TitleBar.styled'
import { ICampaignContextProps } from '@context/Campaign'
import { ISelectedSubmissionsContextProps } from '@context/SelectedSubmissions'
import { ISubmissionListFilterContextProps } from '@context/SubmissionListFilter'
import {
  withAnalyticsTracking,
  withCampaign,
  withSelectedSubmissions,
  withSubmissionListFilter,
} from '@hocs'
import { IApplicationState } from '@store'
import { selectBulkAddFeedbackLink, selectBulkDeclineLink } from '@store/campaigns'
import { selectSubmissionsStats } from '@store/submissions'
import Theme from '@theme'
import { H5 } from '@tribegroup'
import { isEmpty } from '@utils'
import InboxTitleBarActions from './Actions/Actions'
import InboxTitleBarSelectionMode from './SelectionMode/SelectionMode'

interface IInboxTitleBarProps
  extends ISelectedSubmissionsContextProps,
    ICampaignContextProps,
    ISubmissionListFilterContextProps {
  allowSelection?: boolean
  hasEditPermission?: boolean
}

interface IBackLink {
  tooltipText: string
  searchParams?: string
}

export const BackLink = ({ tooltipText }: IBackLink) => (
  <Link
    to={{
      pathname: '/campaigns',
    }}
    data-cy-element="header-back-button"
  >
    <HeaderBackButton
      glyph="nav-back-default"
      size={1.5}
      color={Theme.grey900Color}
      data-tooltip-id="base-tooltip"
      data-tooltip-content={tooltipText}
    />
  </Link>
)

export const InboxTitleBar = ({
  campaign,
  toggleSelectionMode,
  closeSelectionMode,
  isSelectionModeOn,
  emptySelection,
  selectAll,
  filter,
}: IInboxTitleBarProps) => {
  const intl = useIntl()
  const history = useHistory()
  const location = useLocation()

  const campaignId = campaign?.id
  const hasBulkFeedbackPermission = useSelector((state: IApplicationState) =>
    Boolean(selectBulkAddFeedbackLink(state, campaignId)),
  )
  const hasBulkDeclinePermission = useSelector((state: IApplicationState) =>
    Boolean(selectBulkDeclineLink(state, campaignId)),
  )
  const submissionStats = useSelector((state: IApplicationState) =>
    selectSubmissionsStats(state, campaignId),
  )

  const redirectToPreview = () => {
    history.push(`/campaigns/${campaign.id}/preview${location.search}`)
  }

  const onCancel = () => {
    closeSelectionMode()
    emptySelection()
  }

  const onSelectAllSubmissions = () => {
    selectAll()
  }

  if (!campaign) {
    return null
  }

  if (isEmpty(filter)) {
    return null
  }

  const totalSubmissions = (submissionStats[filter] as number) ?? 0

  return (
    <CampaignMenuActionsProvider campaignId={campaign.id}>
      <HeadTag text={campaign.product_name} />
      <TitleBarMobile
        title={<FormattedMessage id={`inbox.sidebar.filters.${filter}`} />}
        count={submissionStats[filter]}
      />

      <TitleBar
        desktopOnly
        isSelectionModeOn={isSelectionModeOn}
        onSelectionOverlayClick={closeSelectionMode}
        actions={
          !isSelectionModeOn && (
            <InboxTitleBarActions
              redirectToPreview={redirectToPreview}
              campaignId={campaign.id}
              toggleSelectionMode={toggleSelectionMode}
            />
          )
        }
      >
        {isSelectionModeOn ? (
          <InboxTitleBarSelectionMode
            campaign={campaign}
            filter={filter}
            onSelectAllSubmissions={onSelectAllSubmissions}
            onCancel={onCancel}
            hasBulkFeedbackPermission={hasBulkFeedbackPermission}
            hasBulkDeclinePermission={hasBulkDeclinePermission}
            totalSubmissions={totalSubmissions}
          />
        ) : (
          <TitleBarContent intl={intl} campaignName={campaign.product_name} />
        )}
      </TitleBar>
    </CampaignMenuActionsProvider>
  )
}

export const TitleBarContent = ({ intl, campaignName }) => {
  return (
    <Fragment>
      <BackLink tooltipText={intl.formatMessage({ id: 'inbox.titlebar.action.back' }) as string} />
      <HeaderTitle data-cy-element="titlebar-call-to-action">
        <H5 color={Theme.grey900Color}>{campaignName}</H5>
      </HeaderTitle>
    </Fragment>
  )
}

export default withCampaign(
  withSelectedSubmissions(withSubmissionListFilter(withAnalyticsTracking(InboxTitleBar))),
)
